import { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageSquareText,
  Bug,
  Lightbulb,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Copy,
  Check,
  X,
  Tag,
  ImageIcon,
  Mail,
} from 'lucide-react';
import {
  feedback,
  FeedbackItem,
  FeedbackDetail,
  FeedbackFilters,
  FeedbackStats,
} from '../api/client';
import { formatNumber, formatRelativeTime, cn } from '../lib/utils';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'wont_fix', label: "Won't Fix" },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'bug', label: 'Bug' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'other', label: 'Other' },
];

const statusColors: Record<string, string> = {
  new: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  reviewed: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  in_progress: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  done: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  wont_fix: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
};

const categoryColors: Record<string, string> = {
  bug: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  suggestion: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  other: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

const categoryIcons: Record<string, typeof Bug> = {
  bug: Bug,
  suggestion: Lightbulb,
  other: MessageSquareText,
};

function formatStatusLabel(status: string): string {
  switch (status) {
    case 'new': return 'New';
    case 'reviewed': return 'Reviewed';
    case 'in_progress': return 'In Progress';
    case 'done': return 'Done';
    case 'wont_fix': return "Won't Fix";
    default: return status;
  }
}

function formatRelativeTimeISO(isoString: string): string {
  return formatRelativeTime(new Date(isoString).getTime());
}

export default function Feedback() {
  // Data state
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [total, setTotal] = useState(0);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [offset, setOffset] = useState(0);

  // Error state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Inline edit state (for expanded row)
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Screenshot detail cache (fetched on expand for items with screenshots)
  const [screenshotCache, setScreenshotCache] = useState<Record<string, string | null>>({});
  const [screenshotLoading, setScreenshotLoading] = useState<string | null>(null);

  // Bulk action state
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkTag, setBulkTag] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Debounce search input (200ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 200);
    return () => clearTimeout(timer);
  }, [searchText]);

  const buildFilters = useCallback((): FeedbackFilters => ({
    category: categoryFilter || undefined,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
    tag: tagFilter || undefined,
    sort: sortOrder,
    limit: PAGE_SIZE,
    offset,
  }), [categoryFilter, statusFilter, debouncedSearch, tagFilter, sortOrder, offset]);

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        feedback.list(buildFilters()),
        feedback.stats(),
      ]);
      setItems(listRes.feedback);
      setTotal(listRes.total);
      setStats(statsRes);
      setErrorMessage(null);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      setErrorMessage('Failed to load feedback data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [buildFilters]);

  // Fetch on filter changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh on a stable 30s interval (uses ref to avoid recreating interval)
  const fetchRef = useRef(fetchData);
  fetchRef.current = fetchData;
  useEffect(() => {
    const interval = setInterval(() => fetchRef.current(), 30000);
    return () => clearInterval(interval);
  }, []);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, categoryFilter, statusFilter, tagFilter, sortOrder]);

  // Scope bulk selection to the current query/page.
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkDeleteConfirm(false);
    setBulkStatus('');
    setBulkTag('');
  }, [debouncedSearch, categoryFilter, statusFilter, tagFilter, sortOrder, offset]);

  // Set edit fields when expanding a row — fetch screenshot if available
  const handleExpand = (item: FeedbackItem) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item.id);
    setEditStatus(item.status);
    setEditNotes(item.admin_notes ?? '');
    setEditTags(item.admin_tags ?? '');
    setDeleteConfirmId(null);

    // Fetch screenshot detail if this item has one and we haven't cached it
    if (item.has_screenshot && !(item.id in screenshotCache)) {
      setScreenshotLoading(item.id);
      feedback.get(item.id).then((detail: FeedbackDetail) => {
        setScreenshotCache((prev) => ({ ...prev, [item.id]: detail.screenshot_base64 }));
      }).catch(() => {
        setScreenshotCache((prev) => ({ ...prev, [item.id]: null }));
      }).finally(() => {
        setScreenshotLoading(null);
      });
    }
  };

  // Show a transient error message that auto-clears after 5s
  const showError = (msg: string) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  // Save inline edits
  const handleSave = async (id: string) => {
    setSaving(true);
    try {
      await feedback.update(id, {
        status: editStatus,
        admin_notes: editNotes,
        admin_tags: editTags,
      });
      await fetchData();
    } catch (error) {
      console.error('Failed to update feedback:', error);
      showError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Delete single
  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await feedback.delete(id);
      setExpandedId(null);
      setDeleteConfirmId(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await fetchData();
    } catch (error) {
      console.error('Failed to delete feedback:', error);
      showError('Failed to delete feedback');
    } finally {
      setDeleting(null);
    }
  };

  // Copy device ID to clipboard
  const handleCopyDeviceId = async (deviceId: string) => {
    try {
      await navigator.clipboard.writeText(deviceId);
      setCopiedId(deviceId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Ignore clipboard errors
    }
  };

  // Selection helpers
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.id)));
    }
  };

  // Bulk actions
  const handleBulkStatusChange = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    try {
      await feedback.bulk({
        ids: Array.from(selectedIds),
        action: 'update_status',
        status: bulkStatus,
      });
      setSelectedIds(new Set());
      setBulkStatus('');
      await fetchData();
    } catch (error) {
      console.error('Bulk status change failed:', error);
      showError('Bulk status change failed');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      await feedback.bulk({
        ids: Array.from(selectedIds),
        action: 'delete',
      });
      setSelectedIds(new Set());
      setBulkDeleteConfirm(false);
      await fetchData();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      showError('Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkAddTag = async () => {
    if (!bulkTag.trim() || selectedIds.size === 0) return;
    try {
      await feedback.bulk({
        ids: Array.from(selectedIds),
        action: 'add_tag',
        tag: bulkTag.trim(),
      });
      setBulkTag('');
      await fetchData();
    } catch (error) {
      console.error('Bulk add tag failed:', error);
      showError('Bulk add tag failed');
    }
  };

  // Pagination
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + PAGE_SIZE, total);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading feedback...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {errorMessage && (
        <div className="flex items-center justify-between bg-rose-500/10 border border-rose-500/30 text-rose-400 px-4 py-3 rounded-xl animate-fade-in-up">
          <span className="text-sm">{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="text-rose-400 hover:text-rose-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Feedback</h1>
          <p className="text-muted-foreground mt-1">User submissions and bug reports</p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Submissions"
          value={formatNumber(stats?.total ?? 0)}
          icon={MessageSquareText}
          color="cyan"
          delay={100}
        />
        <StatCard
          title="New (Unreviewed)"
          value={formatNumber(stats?.by_status?.new ?? 0)}
          icon={MessageSquareText}
          color="violet"
          delay={200}
        />
        <StatCard
          title="Bugs"
          value={formatNumber(stats?.by_category?.bug ?? 0)}
          icon={Bug}
          color="rose"
          delay={300}
        />
        <StatCard
          title="Suggestions"
          value={formatNumber(stats?.by_category?.suggestion ?? 0)}
          icon={Lightbulb}
          color="teal"
          delay={400}
        />
      </div>

      {/* Filter Bar */}
      <div className="card-glow rounded-2xl p-4 animate-fade-in-up" style={{ animationDelay: '500ms' }}>
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search descriptions & notes..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            />
          </div>

          {/* Category */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
          >
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Status */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Tag */}
          {stats?.tags && stats.tags.length > 0 && (
            <select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              className="px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            >
              <option value="">All Tags</option>
              {stats.tags.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          )}

          {/* Sort Toggle */}
          <button
            onClick={() => setSortOrder((prev) => prev === 'newest' ? 'oldest' : 'newest')}
            className="btn-secondary px-3 py-2 rounded-xl text-sm flex items-center gap-1.5"
          >
            {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
          </button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="card-glow rounded-2xl p-4 animate-fade-in-up border border-cyan-500/30">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-cyan-400">
              {selectedIds.size} selected
            </span>

            <div className="h-5 w-px bg-border/50" />

            {/* Bulk Status */}
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="px-3 py-1.5 bg-secondary/50 border border-border/50 rounded-lg text-sm text-foreground focus:outline-none"
            >
              <option value="">Change Status...</option>
              {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkStatusChange}
              disabled={!bulkStatus}
              className="btn-secondary px-3 py-1.5 rounded-lg text-sm disabled:opacity-40"
            >
              Apply
            </button>

            <div className="h-5 w-px bg-border/50" />

            {/* Bulk Tag */}
            <input
              type="text"
              placeholder="Add tag..."
              value={bulkTag}
              onChange={(e) => setBulkTag(e.target.value)}
              className="px-3 py-1.5 bg-secondary/50 border border-border/50 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none w-32"
            />
            <button
              onClick={handleBulkAddTag}
              disabled={!bulkTag.trim()}
              className="btn-secondary px-3 py-1.5 rounded-lg text-sm disabled:opacity-40"
            >
              <Tag className="w-3.5 h-3.5" />
            </button>

            <div className="h-5 w-px bg-border/50" />

            {/* Bulk Delete */}
            {bulkDeleteConfirm ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-rose-400">Delete {selectedIds.size} items?</span>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkDeleting}
                  className="px-3 py-1.5 rounded-lg text-sm bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30 transition-colors"
                >
                  {bulkDeleting ? 'Deleting...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(false)}
                  className="btn-secondary px-3 py-1.5 rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setBulkDeleteConfirm(true)}
                className="px-3 py-1.5 rounded-lg text-sm text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
            )}

            <button
              onClick={() => setSelectedIds(new Set())}
              className="ml-auto btn-secondary px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5"
            >
              <X className="w-3.5 h-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up" style={{ animationDelay: '600ms' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border/50">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={items.length > 0 && selectedIds.size === items.length}
                    onChange={toggleSelectAll}
                    className="rounded border-border/50 bg-secondary/50"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Device</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Version</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tags</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                    No feedback found.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <FeedbackRow
                    key={item.id}
                    item={item}
                    isExpanded={expandedId === item.id}
                    isSelected={selectedIds.has(item.id)}
                    copiedId={copiedId}
                    onToggleExpand={() => handleExpand(item)}
                    onToggleSelect={() => toggleSelect(item.id)}
                    onCopyDeviceId={handleCopyDeviceId}
                    // Screenshot
                    screenshotBase64={screenshotCache[item.id] ?? null}
                    screenshotLoading={screenshotLoading === item.id}
                    // Expanded panel props
                    editStatus={editStatus}
                    editNotes={editNotes}
                    editTags={editTags}
                    setEditStatus={setEditStatus}
                    setEditNotes={setEditNotes}
                    setEditTags={setEditTags}
                    saving={saving}
                    onSave={() => handleSave(item.id)}
                    deleteConfirmId={deleteConfirmId}
                    setDeleteConfirmId={setDeleteConfirmId}
                    deleting={deleting}
                    onDelete={() => handleDelete(item.id)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between animate-fade-in-up" style={{ animationDelay: '700ms' }}>
          <p className="text-sm text-muted-foreground">
            Showing {showingFrom}-{showingTo} of {formatNumber(total)}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={currentPage <= 1}
              className="btn-secondary px-3 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-40"
            >
              <ChevronLeft className="w-4 h-4" />
              Prev
            </button>
            <span className="text-sm text-muted-foreground px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={currentPage >= totalPages}
              className="btn-secondary px-3 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-40"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---

const colorMap: Record<string, { bg: string; text: string; iconBg: string }> = {
  cyan: { bg: 'stat-card-cyan', text: 'text-cyan-400', iconBg: 'bg-cyan-500/20' },
  violet: { bg: 'stat-card-violet', text: 'text-violet-400', iconBg: 'bg-violet-500/20' },
  rose: { bg: '', text: 'text-rose-400', iconBg: 'bg-rose-500/20' },
  teal: { bg: '', text: 'text-teal-400', iconBg: 'bg-teal-500/20' },
};

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  delay,
}: {
  title: string;
  value: string;
  icon: typeof MessageSquareText;
  color: string;
  delay: number;
}) {
  const c = colorMap[color] || colorMap.cyan;
  return (
    <div
      className={cn('card-glow rounded-2xl p-6 animate-fade-in-up', c.bg)}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', c.iconBg)}>
          <Icon className={cn('w-5 h-5', c.text)} />
        </div>
      </div>
      <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
      <p className="text-3xl font-bold tracking-tight">{value}</p>
    </div>
  );
}

function FeedbackRow({
  item,
  isExpanded,
  isSelected,
  copiedId,
  onToggleExpand,
  onToggleSelect,
  onCopyDeviceId,
  screenshotBase64,
  screenshotLoading,
  editStatus,
  editNotes,
  editTags,
  setEditStatus,
  setEditNotes,
  setEditTags,
  saving,
  onSave,
  deleteConfirmId,
  setDeleteConfirmId,
  deleting,
  onDelete,
}: {
  item: FeedbackItem;
  isExpanded: boolean;
  isSelected: boolean;
  copiedId: string | null;
  onToggleExpand: () => void;
  onToggleSelect: () => void;
  onCopyDeviceId: (id: string) => void;
  screenshotBase64: string | null;
  screenshotLoading: boolean;
  editStatus: string;
  editNotes: string;
  editTags: string;
  setEditStatus: (v: string) => void;
  setEditNotes: (v: string) => void;
  setEditTags: (v: string) => void;
  saving: boolean;
  onSave: () => void;
  deleteConfirmId: string | null;
  setDeleteConfirmId: (id: string | null) => void;
  deleting: string | null;
  onDelete: () => void;
}) {
  const CategoryIcon = categoryIcons[item.category] || MessageSquareText;
  const tags = item.admin_tags ? item.admin_tags.split(',').map((t) => t.trim()).filter(Boolean) : [];

  return (
    <>
      {/* Main row */}
      <tr
        className={cn(
          'hover:bg-secondary/30 transition-colors cursor-pointer',
          isExpanded && 'bg-secondary/20',
          isSelected && 'bg-cyan-500/5'
        )}
      >
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onToggleSelect}
            className="rounded border-border/50 bg-secondary/50"
          />
        </td>
        <td className="px-4 py-3" onClick={onToggleExpand}>
          <span className={cn('inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border', statusColors[item.status] || statusColors.new)}>
            {formatStatusLabel(item.status)}
          </span>
        </td>
        <td className="px-4 py-3" onClick={onToggleExpand}>
          <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border', categoryColors[item.category] || categoryColors.other)}>
            <CategoryIcon className="w-3 h-3" />
            {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
          </span>
        </td>
        <td className="px-4 py-3 max-w-[300px]" onClick={onToggleExpand}>
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-foreground truncate">
              {item.description.length > 100 ? item.description.slice(0, 100) + '...' : item.description}
            </p>
            {item.email && (
              <span title={`Email: ${item.email}`}>
                <Mail className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" />
              </span>
            )}
            {item.has_screenshot && (
              <span title="Has screenshot">
                <ImageIcon className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {item.device_id ? (
            <div className="flex items-center gap-1.5">
              <code className="text-xs text-muted-foreground font-mono">
                {item.device_id.slice(0, 8)}...
              </code>
              <button
                onClick={() => onCopyDeviceId(item.device_id!)}
                className="p-1 rounded hover:bg-secondary/50 transition-colors"
                title="Copy device ID"
              >
                {copiedId === item.device_id ? (
                  <Check className="w-3 h-3 text-emerald-400" />
                ) : (
                  <Copy className="w-3 h-3 text-muted-foreground" />
                )}
              </button>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground/50">--</span>
          )}
        </td>
        <td className="px-4 py-3" onClick={onToggleExpand}>
          <div className="text-xs text-muted-foreground">
            {item.app_version && <span>v{item.app_version}</span>}
            {item.app_version && item.ios_version && <span> / </span>}
            {item.ios_version && <span>iOS {item.ios_version}</span>}
            {!item.app_version && !item.ios_version && <span className="text-muted-foreground/50">--</span>}
          </div>
        </td>
        <td className="px-4 py-3" onClick={onToggleExpand}>
          <div className="flex flex-wrap gap-1">
            {tags.length > 0 ? tags.map((tag) => (
              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-medium bg-secondary/50 text-muted-foreground border border-border/30">
                {tag}
              </span>
            )) : (
              <span className="text-xs text-muted-foreground/50">--</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3" onClick={onToggleExpand}>
          <span className="text-xs text-muted-foreground" title={new Date(item.created_at).toLocaleString()}>
            {formatRelativeTimeISO(item.created_at)}
          </span>
        </td>
      </tr>

      {/* Expanded detail panel */}
      {isExpanded && (
        <tr>
          <td colSpan={8} className="px-4 py-0">
            <div className="py-4 px-4 mb-2 mt-1 rounded-xl bg-secondary/20 border border-border/30 space-y-4">
              {/* Full description */}
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Description</h4>
                <p className="text-sm text-foreground whitespace-pre-wrap">{item.description}</p>
              </div>

              {/* Screenshot */}
              {item.has_screenshot && (
                <div>
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Screenshot</h4>
                  {screenshotLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading screenshot...
                    </div>
                  ) : screenshotBase64 ? (
                    <a href={`data:image/jpeg;base64,${screenshotBase64}`} target="_blank" rel="noopener noreferrer">
                      <img
                        src={`data:image/jpeg;base64,${screenshotBase64}`}
                        alt="User screenshot"
                        className="max-w-sm max-h-96 rounded-xl border border-border/30 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                      />
                    </a>
                  ) : (
                    <p className="text-sm text-muted-foreground/50">Failed to load screenshot</p>
                  )}
                </div>
              )}

              {/* Metadata grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <MetaField label="Device ID" value={item.device_id ?? '--'} mono />
                <MetaField label="App Version" value={item.app_version ? `v${item.app_version}` : '--'} />
                <MetaField label="iOS Version" value={item.ios_version ?? '--'} />
                <MetaField label="Device Model" value={item.device_model ?? '--'} />
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-0.5">Email</p>
                  {item.email ? (
                    <a href={`mailto:${item.email}`} className="text-sm text-cyan-400 hover:text-cyan-300 underline underline-offset-2 break-all">
                      {item.email}
                    </a>
                  ) : (
                    <p className="text-sm text-foreground">--</p>
                  )}
                </div>
              </div>

              {/* Edit fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Status */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                  >
                    {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Admin Tags */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={editTags}
                    onChange={(e) => setEditTags(e.target.value)}
                    placeholder="crash, ui, priority..."
                    className="w-full px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
                  />
                </div>

                {/* Timestamps */}
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Timestamps</label>
                  <div className="text-xs text-muted-foreground space-y-1 pt-1">
                    <p>Created: {new Date(item.created_at).toLocaleString()}</p>
                    <p>Updated: {new Date(item.updated_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">Admin Notes</label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add admin notes..."
                  rows={3}
                  className="w-full px-3 py-2 bg-secondary/50 border border-border/50 rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-cyan-500/30 resize-y"
                />
              </div>

              {/* Action buttons */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-cyan-500/20 to-violet-500/20 text-cyan-400 border border-cyan-500/30 hover:from-cyan-500/30 hover:to-violet-500/30 transition-all disabled:opacity-40 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Save Changes
                    </>
                  )}
                </button>

                {deleteConfirmId === item.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-rose-400">Delete this feedback?</span>
                    <button
                      onClick={onDelete}
                      disabled={deleting === item.id}
                      className="px-3 py-2 rounded-xl text-sm bg-rose-500/20 text-rose-400 border border-rose-500/30 hover:bg-rose-500/30 transition-colors"
                    >
                      {deleting === item.id ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirmId(null)}
                      className="btn-secondary px-3 py-2 rounded-xl text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteConfirmId(item.id)}
                    className="px-4 py-2 rounded-xl text-sm text-rose-400 hover:bg-rose-500/10 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}

                <button
                  onClick={onToggleExpand}
                  className="ml-auto btn-secondary px-4 py-2 rounded-xl text-sm flex items-center gap-2"
                >
                  <X className="w-3.5 h-3.5" />
                  Close
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function MetaField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={cn('text-sm text-foreground', mono && 'font-mono text-xs break-all')}>{value}</p>
    </div>
  );
}
